// QA Search Bar Component
// Search bar for finding and adding Q&A items to featured content

import React, { useState, useEffect } from 'react';
import { logger } from '../../../core';
import './QASearchBar.css';

export function QASearchBar({ onAddQA }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/api/learn/questions?search=${encodeURIComponent(query.trim())}&limit=10`);
      const data = await response.json();

      if (data.success && data.qa) {
        setSearchResults(data.qa);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|featured|qa-search|error',
        color1: 'red',
      }, 'QA Search error:', error);
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddQA = (qa) => {
    onAddQA(qa);
    setSearchQuery('');
    setShowResults(false);

    logger.log({
      tags: 'admin|featured|qa-add',
      color1: 'green',
    }, `Added Q&A to featured content: ${qa.question}`);
  };

  const handleSearchFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow clicking
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="qa-search-bar">
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search for Q&A to feature..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
        />

        {isSearching && (
          <div className="search-loading">
            <i className="fa-solid fa-spinner fa-spin"></i>
          </div>
        )}
      </div>

      {/* Search results dropdown */}
      {showResults && searchResults.length > 0 && (
        <div className="search-results-dropdown">
          {searchResults.map((qa) => (
            <button
              key={qa.id}
              className="search-result-item"
              onClick={() => handleAddQA(qa)}
            >
              <div className="result-info">
                <span className="result-title">{qa.question}</span>
                <span className="result-description">
                  {qa.answer.length > 60
                    ? `${qa.answer.substring(0, 60)}...`
                    : qa.answer
                  }
                </span>
              </div>
              <i className="fa-solid fa-plus add-icon"></i>
            </button>
          ))}
        </div>
      )}

      {showResults && searchResults.length === 0 && searchQuery.trim() && !isSearching && (
        <div className="search-no-results">
          <i className="fa-solid fa-search"></i>
          <p>No Q&A found for "{searchQuery}"</p>
          <small>Try different keywords</small>
        </div>
      )}
    </div>
  );
}